import type { FC, ReactNode } from "react";
import {
  useContext,
  createContext,
  useCallback,
  useEffect,
  useState
} from "react";
import { get as lodashGet, isEqual } from "lodash-es";
import {
  useCommentsInfiniteQuery,
  useCommentsQuery
} from "@certa/queries/hooks/comments.hooks";
import { workflowServices } from "@certa/queries/services/workflow.services";
import type {
  AdjudicationStatus,
  CommentChannel,
  DropdownStatusDefault,
  WorkflowFamily
} from "@certa/types";
import { CommentObjectTypes } from "@certa/types";
import { queryClient } from "@certa/queries/utils/utils";
import { useCommentsDrawerController } from "./hooks/useCommentsDrawerController";
import { CommentsDrawerView } from "./components/CommentsDrawerView";
import { useGetConversationsComms } from "@certa/queries/queries/comms";
import type { InfiniteConversationCommsModel } from "./components/commentsNew/ConversationListItemAppRedesign";
import { useCommsNewEndpoints } from "@certa/common/toggles";

export type TUseComments = {
  objectId: number | undefined;
  messageId?: string;
  objectType: CommentObjectTypes;
  channel?: CommentChannel;
  onStepClick?: (groupId: number, stepId: number, workflowId: number) => void;
  uid?: string;
  extra?: any;
  readOnly?: boolean;
  threadId?: number;
  header: React.ReactChild;
  defaultStatus?: DropdownStatusDefault;
  defaultAdjudicationCode?: AdjudicationStatus;
  adjudicationRiskCodes?: string[];
  workflowFamily?: WorkflowFamily;
  multiChannelWithTabs?: boolean;
  isWorkflowBeingMigrated?: boolean;
  isNonEditable?: boolean;
};

export enum DrawerViewStates {
  NOT_OPENED = "not-opened",
  OPENED = "opened",
  CLOSED = "closed"
}

export type TCommentsDrawerContext = {
  commentsDrawerStates: DrawerViewStates;
  showComments: (data: TUseComments) => void;
  onClose: () => void;
  isWorkflowBeingMigrated?: boolean;
  isNonEditable?: boolean;
  parentThreadObject?: TUseComments | undefined;
  shouldShowNewThread: boolean;
  setShouldShowNewThread: (shouldShowNewThread: boolean) => void;
  isReadOnly: boolean;
};

export const CommentsDrawerContext = createContext<
  TCommentsDrawerContext | undefined
>(undefined);

/**
 * CommentsContextProvider is used to open comments drawer from mentions as well as
 * from comments anywhere in the app. This mainly ensures the comments data is passed to the
 * comments drawer and the drawer is opened accordingly.
 */

export const CommentsContextProvider: FC<{
  children: ReactNode;
}> = props => {
  const {
    isMultiChannel,
    multiChannelWithTabs: isMultiChannelWithTabs,
    activeChannel,
    channels,
    commentsData,
    show: shouldShow,
    drawerStates,
    isWorkflowBeingMigrated,
    isNonEditable,
    setIsMultiChannel,
    setMultiChannelWithTabs,
    setActiveChannel,
    setChannels,
    setCommentsData,
    setShow,
    setDrawerStates,
    setIsWorkflowBeingMigrated,
    setIsNonEditable,
    activeConversationThreadComms,
    setActiveConversationThreadComms,
    onClose
  } = useCommentsDrawerController();

  const {
    objectId,
    objectType,
    uid,
    header = "",
    adjudicationRiskCodes,
    defaultAdjudicationCode,
    defaultStatus,
    extra,
    messageId,
    readOnly: isReadOnly,
    threadId,
    workflowFamily,
    onStepClick
  } = commentsData || {};

  const workflowId =
    objectType === CommentObjectTypes.WORKFLOW ||
    objectType === CommentObjectTypes.CONSOLIDATED
      ? objectId
      : channels?.[0]?.workflowId;

  const { data: threadsCommsData, ...threadsCommsQueryResult } =
    useGetConversationsComms(workflowId, objectType, objectId);

  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  /** Common parameters shared between useCommentsInfiniteQuery and useCommentsQuery */
  const commentsQueryParams = {
    objectId: objectId || 0,
    objectType: objectType || CommentObjectTypes.STEP,
    uid,
    config: {
      cacheTime: 0, // To avoid the previous comments data shown in split second
      enabled: shouldShow && !!objectId && !!objectType
    }
  };

  const commentsInfiniteQuery = useCommentsInfiniteQuery({
    ...commentsQueryParams,
    config: {
      ...commentsQueryParams.config,
      enabled: commentsQueryParams.config.enabled && isCommsNewEndpointEnabled
    }
  });

  const threadsCommsLoadMore = async () => {
    if (
      !commentsInfiniteQuery.isFetching &&
      !threadsCommsQueryResult?.isFetching
    ) {
      /**
       * Fetch next page of channels first as the query is slower than comms query
       */
      await commentsInfiniteQuery.fetchNextPage();
      await threadsCommsQueryResult.fetchNextPage();
    }
  };

  const threadsComms: InfiniteConversationCommsModel | undefined =
    threadsCommsData
      ? {
          data: threadsCommsData,
          ...threadsCommsQueryResult,
          loadMore: threadsCommsLoadMore
        }
      : undefined;

  const commentsQuery = useCommentsQuery({
    ...commentsQueryParams,
    config: {
      ...commentsQueryParams.config,
      enabled: commentsQueryParams.config.enabled && !isCommsNewEndpointEnabled
    }
  });

  const {
    data,
    status,
    refetch: fetchComments
  } = isCommsNewEndpointEnabled ? commentsInfiniteQuery : commentsQuery;

  // Makes sure comments are updated in multi channel view
  useEffect(() => {
    if (channels?.length && activeChannel) {
      const existingChannelIndex = channels?.findIndex(
        channel => channel?.objectId === activeChannel?.objectId
      );
      // Only runs when there is a diff between existing channel and incoming channel data
      if (
        existingChannelIndex > -1 &&
        !isEqual(activeChannel, channels[existingChannelIndex])
      ) {
        setActiveChannel(channels[existingChannelIndex]);
      }
    }
  }, [activeChannel, channels, setActiveChannel]);

  useEffect(() => {
    if (data?.results && !isCommsNewEndpointEnabled) {
      setChannels(data?.results);
    }
    if (data?.pages && isCommsNewEndpointEnabled) {
      setChannels(data?.pages.flatMap((x: any) => x.results));
    }
  }, [data?.results, data?.pages, setChannels, isCommsNewEndpointEnabled]);

  useEffect(() => {
    if (!shouldShow && drawerStates === DrawerViewStates.CLOSED) {
      setDrawerStates(DrawerViewStates.NOT_OPENED);
    }
  }, [shouldShow, drawerStates, setDrawerStates]);

  /**
   * To open comments drawer in single channel mode
   */

  const showComments = useCallback(
    async (commentsData: TUseComments) => {
      if (!commentsData.objectId || !commentsData.objectType) {
        console.error("objectID or objectType must be specified");
        return;
      }

      setCommentsData(commentsData);
      setActiveConversationThreadComms({
        objectId: commentsData.objectId.toString(),
        objectType: commentsData.objectType
      });
      fetchComments().then(({ data }) => {
        const response = lodashGet(data, "results[0]", {});
        const { objectId: id, objectType: type } = response;

        if (id !== commentsData.objectId || commentsData.objectType !== type) {
          const key = `comments-${type}-${commentsData.objectId}`;

          queryClient.removeQueries(key);
          queryClient.setQueryData(key, () => data);
        }
      });
      setShow(true);
      setDrawerStates(DrawerViewStates.OPENED);
      setIsMultiChannel(
        commentsData.objectType === CommentObjectTypes.CONSOLIDATED
      );
      setMultiChannelWithTabs(!!commentsData.multiChannelWithTabs);
      // Checks if the workflows are being migrated or not.
      // P.S: The data is being cached so no extra API calls!
      const { isWorkflowBeingMigrated, isNonEditable } =
        await workflowServices.lazyFetchProcessDetails(commentsData.objectId);

      if (
        isWorkflowBeingMigrated !== undefined ||
        commentsData?.isWorkflowBeingMigrated !== undefined
      ) {
        setIsWorkflowBeingMigrated(
          !!commentsData?.isWorkflowBeingMigrated || !!isWorkflowBeingMigrated
        );
      }
      if (
        isNonEditable !== undefined ||
        commentsData?.isNonEditable !== undefined
      ) {
        setIsNonEditable(!!commentsData?.isNonEditable || !!isNonEditable);
      }
    },
    [
      setCommentsData,
      setActiveConversationThreadComms,
      fetchComments,
      setShow,
      setDrawerStates,
      setIsMultiChannel,
      setMultiChannelWithTabs,
      setIsWorkflowBeingMigrated,
      setIsNonEditable
    ]
  );

  const activeFieldName =
    activeConversationThreadComms?.path?.fieldName ||
    activeChannel?.fieldName ||
    channels?.find(channel => channel.objectId === commentsData?.objectId)
      ?.fieldName;

  const [shouldShowNewThread, setShouldShowNewThread] = useState(false);

  return (
    <CommentsDrawerContext.Provider
      value={{
        commentsDrawerStates: drawerStates,
        showComments,
        onClose,
        isWorkflowBeingMigrated,
        isNonEditable,
        parentThreadObject: commentsData,
        shouldShowNewThread,
        setShouldShowNewThread,
        isReadOnly: !!isReadOnly
      }}
    >
      {props.children}
      <CommentsDrawerView
        shouldShow={shouldShow}
        status={status}
        channels={channels}
        onStepClick={onStepClick}
        isReadOnly={isReadOnly}
        extra={extra}
        threadId={threadId}
        header={header}
        onClose={onClose}
        activeChannel={activeChannel}
        setActiveChannel={setActiveChannel}
        threadsComms={threadsComms}
        threadsCommsStatus={threadsCommsQueryResult.status}
        activeConversationThreadComms={activeConversationThreadComms}
        setActiveConversationThreadComms={setActiveConversationThreadComms}
        messageId={messageId}
        defaultStatus={defaultStatus}
        defaultAdjudicationCode={defaultAdjudicationCode}
        adjudicationRiskCodes={adjudicationRiskCodes}
        workflowFamily={workflowFamily}
        isMultiChannel={isMultiChannel}
        activeFieldName={activeFieldName}
        multiChannelWithTabs={isMultiChannelWithTabs}
      />
    </CommentsDrawerContext.Provider>
  );
};

export const useCommentsDrawerContext = () => {
  const contextValue = useContext(CommentsDrawerContext);
  if (typeof contextValue === "undefined") {
    throw new Error("No value defined for the comments drawer context");
  }

  return contextValue;
};

/**
 * useCommentsDrawerClose is used to update the data when the comments drawer is closed.
 *
 * This has been added to support backward compatibility of useOverlay -> useOverlayOnClose.😢
 *
 * @param onClose
 * @returns
 */
export const useCommentsDrawerClose = (onClose: () => void) => {
  const { commentsDrawerStates } = useCommentsDrawerContext();

  useEffect(() => {
    if (commentsDrawerStates === DrawerViewStates.CLOSED) {
      onClose();
    }
  }, [commentsDrawerStates, onClose]);
};
